/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { Table, Modal, Space, Alert, Button, Input } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';

import type { TableColumnsType } from 'antd';

import { grpc } from '@improbable-eng/grpc-web';
import { GetUsersRequest, GetUsersResponse } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import { ServiceError } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb_service';
import { withAuth, AuthContextProps } from '../../LocalAuthWrapper';
import Paragraph from 'antd/es/typography/Paragraph';
import UserListGroupButton from './UserListGroupButton';
import StreamkeyModal from './StreamKeyModal';
import { createClient, setKappiOwner, updateGroupMembership } from './utils';
import UserListFilter from './UserListFilter';
import { User } from '../../types';

import './UserList.css';
import UserListGroupFilter from './UserListGroupFilter';

type UserListProps = {} & AuthContextProps;

type AllowedGroup = 'tiro/admin' | 'tal-tiro-is:rauntimatextun';

type GroupStatus = {
  [key in AllowedGroup]: boolean;
};

interface GroupModification {
  group: AllowedGroup;
  join: boolean;
}

interface Waiting {
  active: boolean;
  user?: string;
  group?: AllowedGroup;
}

const { confirm } = Modal;
const { Search } = Input;

// TODO(Smári, RAUN-217): Move state to an object.
// TODO(Smári): Fetch and display group membership info like we do for stream keys.
//              The way this is done now is very slow.
function UserList(props: UserListProps) {
  const [streamKeysUserId, setStreamKeysUserId] = useState<string | undefined>(undefined);
  const [userList, setUserList] = useState<User[]>([]);
  const [filterActive, setFilterActive] = useState(false);
  const [filteredUserList, setFilteredUserList] = useState<User[]>([]);
  const [searchFilter, setSearchFilter] = useState<string | undefined>(undefined);
  const [searchFilterStr, setSearchFilterStr] = useState<string>('');
  const [noResults, setNoResults] = useState<boolean>(false);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [groupFilter, setGroupFilter] = useState<GetUsersRequest.GroupFilter[]>([
    GetUsersRequest.GroupFilter.GROUP_UNSPECIFIED,
  ]);
  const [pageNum, setPageNum] = useState<number>(1);
  const [nextPageNum, setNextPageNum] = useState<number>(2);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [searching, setSearching] = useState<boolean>(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [waiting, setWaiting] = useState<Waiting>({ active: false });

  const history = useHistory();
  const location = useLocation();

  /** Shows the confirmation modal for adding or removing a user from a group.
   *
   * @param userData Information about the user
   * @param name Information about which group membership to change for user
   */
  const showUpdateConfirm = (userData: User, groupChange: GroupModification) => {
    const currentGroups: GroupStatus = {
      'tiro/admin': userData.groupsList.includes('tiro/admin'),
      'tal-tiro-is:rauntimatextun': userData.groupsList.includes('tal-tiro-is:rauntimatextun'),
    };

    confirm({
      title: `${groupChange.join ? 'Bæta við í' : 'Fjarlægja úr'} hóp.`,
      content: (
        <Paragraph>
          {`Ertu viss um að þú viljir ${groupChange.join ? 'bæta' : 'fjarlægja'} ` +
            `${userData.name}(${userData.userId}) ` +
            `${groupChange.join ? 'við í hópinn' : 'úr hópnum'}: „${groupChange.group}“?`}
        </Paragraph>
      ),
      icon: <ExclamationCircleFilled />,
      okText: groupChange.join ? 'Bæta við' : 'Fjarlægja',
      okType: 'primary',
      cancelText: 'Hætta við',
      width: '500px',
      onOk() {
        currentGroups[groupChange.group] = groupChange.join;
        changeGroupMembership(
          userData.userId,
          groupChange.group,
          Object.keys(currentGroups)
            .map((K) => {
              if (currentGroups[K as keyof typeof currentGroups]) {
                return K;
              }
            })
            .filter((group) => group !== undefined) as string[],
        );
        groupChange.join &&
          groupChange.group === 'tal-tiro-is:rauntimatextun' &&
          makeUserKappiOwner(userData.userId, userData.name, userData.email);
      },
      onCancel() {
        console.log('Group change cancelled by user.');
      },
    });
  };

  const userColumns: TableColumnsType<User> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'User Id',
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Groups',
      key: 'groups',
      dataIndex: 'groupsList',
      render: (groupsList, userRecord) => (
        <Space direction='vertical' size='small'>
          <UserListGroupButton
            title='Admin'
            checked={groupsList.includes('tiro/admin')}
            onChange={(e) => {
              showUpdateConfirm(userRecord, { group: 'tiro/admin', join: e });
            }}
            loading={waiting.active && waiting.user === userRecord.userId && waiting.group === 'tiro/admin'}
            disabled={waiting.active}
          />
          <UserListGroupButton
            title='RTC'
            checked={groupsList.includes('tal-tiro-is:rauntimatextun')}
            onChange={(e) => {
              showUpdateConfirm(userRecord, { group: 'tal-tiro-is:rauntimatextun', join: e });
            }}
            loading={
              waiting.active && waiting.user === userRecord.userId && waiting.group === 'tal-tiro-is:rauntimatextun'
            }
            disabled={waiting.active}
          />
        </Space>
      ),
    },
    {
      title: 'Owned Kappi stream keys',
      key: 'showStreamKeys',
      render: (record) => (
        <Button
          type='primary'
          onClick={() => {
            setStreamKeysUserId(record.userId);
            setModalOpen(true);
          }}
        >
          {' '}
          Stream keys
        </Button>
      ),
    },
  ];

  /** Add or remove user from a group in keycloak then update user list after a short while.
   * @param userId keycloak user id
   */
  const changeGroupMembership = (userId: string, changedGroupName: AllowedGroup, currentGroups: string[]) => {
    updateGroupMembership(userId, currentGroups, props.authCtx.accessToken!);

    // The data from transcriber->keycloak is not always immediately up to date. Hence the wait.
    setWaiting({ active: true, user: userId, group: changedGroupName });
    setTimeout(() => {
      getUsers();
      setWaiting({ active: false, user: undefined, group: undefined });
    }, 5000);
  };

  /** Sends a post request to Kappi and adds the user as an Owner
   *
   * @param userId keycloak user id
   * @param name keycloak user name
   * @param email keycloak user email
   */
  const makeUserKappiOwner = async (userId: string, name: string, email: string) => {
    const result: { success: boolean; message?: string } = await setKappiOwner(
      userId,
      name,
      email,
      props.authCtx.accessToken!,
    );
    if (!result.success) {
      setError(result.message ? result.message : 'An unknown error occurred when adding user as a Kappi owner.');
    } else {
      console.log('User added as Kappi owner.');
      result.message && setInfo(result.message);
    }
  };

  /**
   * Gets and sets all keycloak users
   */
  const getUsers = (isSearching: boolean = false) => {
    isSearching && setSearching(true);

    setUserList([]);
    setLoadingUsers(true);
    const client = createClient();
    const req = new GetUsersRequest();
    req.setPageToken(pageNum.toString());
    req.setPageSize(pageSize);
    req.setGroupFilterList(groupFilter);
    searchFilter && req.setFilter(searchFilter);

    client.getUsers(
      req,
      new grpc.Metadata(
        props.authCtx.accessToken
          ? {
              Authorization: `Bearer ${props.authCtx.accessToken}`,
            }
          : {},
      ),
      (error: ServiceError | null, msg: GetUsersResponse | null) => {
        if (msg) {
          const users = msg.getUsersList().map((user) => user.toObject());
          setNextPageNum(Number(msg.getNextPageToken()));
          setTotalAmount(msg.getTotalSize());
          setNoResults(users.length === 0);
          setUserList(users);
        } else if (error) {
          console.log(error);
        }
        isSearching && setSearching(false);
        setLoadingUsers(false);
      },
    );
  };

  useEffect(() => {
    const queryParam: string | null = new URLSearchParams(location.search).get('search');
    if (queryParam) {
      setSearchFilterStr(queryParam);
      setSearchFilter(queryParam);
    }
  }, []);

  useEffect(() => {
    getUsers();
  }, [pageNum, pageSize, totalAmount]);

  useEffect(() => {
    let stateModified = false;
    if (pageNum !== 1) {
      setPageNum(1);
      stateModified = true;
    }
    if (searchFilter) {
      setSearchFilter(undefined);
      searchFilterStr.length > 1 && setSearchFilterStr('');
      stateModified = true;
      history.replace({ pathname: location.pathname, search: '' });
    }
    !stateModified && getUsers();
  }, [groupFilter]);

  useEffect(() => {
    getUsers(true);
  }, [searchFilter]);

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFilterStr(e.target.value);
  };

  const onSearch = () => {
    const shouldFilter: boolean = searchFilterStr.length > 0;

    setPageNum(1);
    setSearchFilter(shouldFilter ? searchFilterStr : undefined);

    if (shouldFilter) {
      const params = new URLSearchParams({ search: searchFilterStr });
      history.replace({ pathname: location.pathname, search: params.toString() });
    }
  };

  const onPaginationChange = (page: number, pageSize?: number) => {
    setPageNum(page);
    pageSize && setPageSize(pageSize);
  };

  return (
    <div
      style={{
        width: '90%',
        margin: '0 auto',
        justifyContent: 'center',
      }}
    >
      <h3>Users</h3>

      {!!error && (
        <Space direction='vertical' style={{ margin: '1rem' }}>
          <Alert
            message='Það kom upp villa!'
            description={`Status code: - ${error}`}
            type='error'
            showIcon
            onClose={() => setError('')}
            closable
          />
        </Space>
      )}
      {!!info && (
        <Space direction='vertical' style={{ margin: '1rem' }}>
          <Alert message='Athugið!' description={info} type='info' showIcon onClose={() => setInfo('')} closable />
        </Space>
      )}
      {modalOpen && streamKeysUserId && (
        <StreamkeyModal
          open={modalOpen}
          handleClose={() => {
            setModalOpen(false);
            setStreamKeysUserId(undefined);
          }}
          userId={streamKeysUserId}
          accessToken={props.authCtx.accessToken!}
        />
      )}
      <div className='filter-container'>
        <Search
          placeholder={`Search for users...`}
          value={searchFilterStr}
          onChange={onSearchChange}
          onSearch={onSearch}
          loading={searching}
          style={{ width: '30%' }}
          disabled={groupFilter.length > 0 && groupFilter[0] !== GetUsersRequest.GroupFilter.GROUP_UNSPECIFIED}
          allowClear
        />
        <UserListFilter
          unfilteredUserList={userList}
          setFilteredUserList={setFilteredUserList}
          setFilterActive={setFilterActive}
        />
        <UserListGroupFilter setGroupFilter={setGroupFilter} />
      </div>
      <Table
        columns={userColumns}
        size='middle'
        dataSource={filterActive ? filteredUserList : userList}
        rowKey={'userId'}
        loading={loadingUsers}
        pagination={{
          defaultCurrent: 1,
          defaultPageSize: 10,
          current: pageNum,
          pageSize: pageSize,
          showQuickJumper: true,
          showSizeChanger: true,
          onChange: onPaginationChange,
          total: totalAmount,
        }}
        locale={{ emptyText: noResults ? 'No results!' : '' }}
        responsive={false}
      />
    </div>
  );
}

export default withAuth(UserList);
